<template>
  <div v-if="enabled" class="quantity-dropdown-wrapper">
    <v-select
      :key="dropdownKey"
      :value="currentValue"
      :items="dropdownItems"
      @change="onDropdownChange"
      outlined
      hide-details
      class="quantity-dropdown"
      item-text="text"
      item-value="value"
      :menu-props="{
        top: true,
        offsetY: true,
        contentClass: 'quantity-dropdown-menu',
        maxHeight: 400,
        minWidth: 200
      }"
    >
    </v-select>

    <!-- Custom Quantity Dialog -->
    <custom-quantity-dialog
      :is-open="customDialogOpen"
      :current-quantity="selectedQuantity"
      @custom-quantity-selected="onCustomQuantitySelected"
      @close="onCustomDialogClose"
    ></custom-quantity-dialog>
  </div>
</template>

<script>
import CustomQuantityDialog from '../dialogs/CustomQuantityDialog.vue'

export default {
  name: 'QuantityDropdown',

  components: {
    CustomQuantityDialog
  },

  props: {
    selectedQuantity: {
      type: Number,
      default: 1
    },
    enabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      customDialogOpen: false,
      presetValues: [0.1, 0.2, 0.5, 1, 1.5],
      dropdownKey: 0
    }
  },

  computed: {
    dropdownItems () {
      const items = this.presetValues.map(val => ({
        text: `×${val}`,
        value: val
      }))

      // If current quantity is not in preset values, add it to the list
      if (!this.presetValues.includes(this.selectedQuantity) && this.selectedQuantity !== null) {
        const displayValue = Number.isInteger(this.selectedQuantity)
          ? this.selectedQuantity
          : this.selectedQuantity.toFixed(1)
        items.push({
          text: `×${displayValue}`,
          value: this.selectedQuantity
        })
      }

      // Add "Custom" option at the end
      items.push({
        text: 'Prilagođeno...',
        value: 'custom'
      })

      return items
    },

    currentValue () {
      // Return the current quantity value for the dropdown
      return this.selectedQuantity
    }
  },

  watch: {
    selectedQuantity (newVal) {
      // Watch for changes from parent
      if (newVal !== this.currentValue) {
        this.$forceUpdate()
      }
    }
  },

  methods: {
    onDropdownChange (value) {
      if (value === 'custom') {
        // Open custom dialog
        this.customDialogOpen = true
        // Keep the dropdown at the previous value (don't change selectedQuantity)
      } else {
        // User selected a preset value
        this.$emit('quantity-changed', value)
      }
    },

    onCustomQuantitySelected (quantity) {
      this.$emit('quantity-changed', quantity)
      this.customDialogOpen = false
    },

    onCustomDialogClose () {
      this.customDialogOpen = false
      // Force v-select to re-render by changing its key
      this.dropdownKey++
    }
  }
}
</script>

<style scoped>
.quantity-dropdown-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
}

.quantity-dropdown {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  flex: 1;
  margin: 0 !important;
  padding: 0 !important;
}

.quantity-dropdown >>> .v-input {
  width: 100% !important;
  max-width: 100% !important;
}

/* Make dropdown look like glass buttons */
.quantity-dropdown >>> .v-input__control {
  min-height: 3.5rem !important;
  height: 3.5rem !important;
  width: 100% !important;
  max-width: 100% !important;
}

.quantity-dropdown >>> .v-input__slot {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.quantity-dropdown >>> .v-text-field__details {
  display: none;
  padding: 0 !important;
}

.quantity-dropdown >>> fieldset {
  border: none !important;
  border-radius: 12px !important;
  height: 3.5rem !important;
}

.quantity-dropdown >>> .v-select__slot {
  background: #345778;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 0 16px !important;
  min-height: 3.5rem !important;
  height: 3.5rem !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #345778;
}

.quantity-dropdown >>> .v-select__selection {
  font-weight: 600;
  color: white;
  font-size: 18px;
  margin-top: 0 !important;
}

.quantity-dropdown >>> .v-input__icon--prepend-inner {
  margin-top: 0 !important;
  align-self: center;
}

.quantity-dropdown >>> .v-input__icon--prepend-inner .v-icon {
  color: white !important;
}

.quantity-dropdown >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center;
}

.quantity-dropdown >>> .v-input__append-inner .v-icon {
  color: white !important;
}

.quantity-dropdown >>> .v-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  top: 6px !important;
}

.quantity-dropdown >>> .v-label--active {
  transform: translateY(-22px) scale(0.85);
  color: white !important;
}

/* Hover effect */
.quantity-dropdown:hover >>> fieldset {
  border-color: rgba(0, 179, 190, 0.6) !important;
}

.quantity-dropdown:hover >>> .v-select__slot {
  background: #3d6489;
  box-shadow: 0 4px 12px rgba(52, 87, 120, 0.4);
}

/* Focus effect */
.quantity-dropdown >>> .v-input--is-focused fieldset {
  border-color: rgba(0, 179, 190, 0.6) !important;
}
</style>

<style>
/* Global styles for the dropdown menu (not scoped) */
.quantity-dropdown-menu {
  margin-top: 8px;
}

.quantity-dropdown-menu .v-list {
  background: #345778 !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 2px solid rgba(52, 87, 120, 0.5);
  border-radius: 8px;
  padding: 8px 0;
  box-shadow: 0 8px 24px rgba(52, 87, 120, 0.4);
}

.quantity-dropdown-menu .v-list-item {
  min-height: 56px !important;
  padding: 12px 24px !important;
  font-size: 18px !important;
  font-weight: 600;
  color: white;
  transition: all 0.2s ease;
}

.quantity-dropdown-menu .v-list-item:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  transform: translateX(4px);
}

.quantity-dropdown-menu .v-list-item--active {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  font-weight: 700;
}

.quantity-dropdown-menu .v-list-item__title {
  font-size: 18px !important;
  font-weight: 600;
  color: white !important;
}
</style>
